import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { expertAuthApi } from '../lib/http';
import logo from '../assets/logo.png';
import ProfileSwitcher from './ProfileSwitcher';

interface ExpertLayoutProps {
  children: ReactNode;
}

interface Expert {
  id: string;
  email: string;
  display_name: string;
  timezone: string;
  account_id: string;
  created_at: string;
}

const ExpertLayout: React.FC<ExpertLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expert, setExpert] = useState<Expert | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    // Load expert info from session storage
    const storedExpert = sessionStorage.getItem('expert');
    if (storedExpert) {
      try {
        setExpert(JSON.parse(storedExpert));
      } catch (e) {
        console.error('Failed to parse expert data:', e);
      }
    }

    // Fetch current session from API
    const fetchSession = async () => {
      try {
        const response = await expertAuthApi.session();
        setExpert(response.data.expert);
        sessionStorage.setItem('expert', JSON.stringify(response.data.expert));
      } catch (error: any) {
        console.error('Failed to fetch session:', error);

        // Only redirect to login on authentication errors (401, 403)
        // Don't redirect on rate limit (429) or network errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/expert/login');
        }
        // For other errors (429, network, etc), keep existing cached expert data
      }
    };

    fetchSession();
  }, [navigate]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogoutClick = () => {
    setMobileMenuOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutConfirm(false);
    try {
      await expertAuthApi.signout();
      sessionStorage.removeItem('sessionRole');
      sessionStorage.removeItem('expert');
      navigate('/auth/select');
    } catch (error) {
      console.error('Logout failed:', error);
      // Force logout on client side even if server request fails
      sessionStorage.removeItem('sessionRole');
      sessionStorage.removeItem('expert');
      navigate('/auth/select');
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Primary nav items (shown in bottom nav on mobile)
  const primaryNavItems = [
    { path: '/app/dashboard', label: 'Calendar', icon: CalendarIcon },
    { path: '/app/bookings', label: 'Bookings', icon: BookingsIcon },
    { path: '/app/clients', label: 'Clients', icon: ClientsIcon },
    { path: '/app/services', label: 'Services', icon: ServicesIcon },
  ];

  // Secondary nav items (shown in "More" menu on mobile)
  const secondaryNavItems = [
    { path: '/app/working-hours', label: 'Working Hours', icon: ClockIcon },
    { path: '/app/integrations', label: 'Integrations', icon: IntegrationsIcon },
    { path: '/app/settings', label: 'Settings', icon: SettingsIcon },
  ];

  const allNavItems = [...primaryNavItems, ...secondaryNavItems];

  const isActive = (path: string) => location.pathname === path;
  const isSecondaryActive = secondaryNavItems.some(item => isActive(item.path));

  const getExpertInitials = (): string => {
    if (!expert) return 'P';
    const name = expert.display_name || expert.email;
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex w-64 bg-white border-r border-gray-200 flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center mb-1">
            <img src={logo} alt="BookDay" className="h-10 w-auto" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Expert Console</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {allNavItems.map((item) => (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-pink-50 text-pink-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'text-pink-600' : 'text-gray-400'}`} />
                  <span>{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-200 space-y-3">
          {/* Profile Switcher */}
          <ProfileSwitcher currentProfile="expert" />

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-semibold">
              {getExpertInitials()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-800 truncate">
                {expert?.display_name || 'Expert'}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {expert?.email || ''}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogoutClick}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="flex-1 flex flex-col md:hidden">
        {/* Mobile Header */}
        <header className="bg-white/95 backdrop-blur-lg sticky top-0 z-40 border-b border-gray-100">
          <div className="flex items-center justify-between h-14 px-4">
            <img src={logo} alt="BookDay" className="h-8 w-auto" />

            {/* Profile Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-semibold text-sm">
                {getExpertInitials()}
              </div>
            </button>
          </div>
        </header>

        {/* Mobile Content */}
        <main className="flex-1 overflow-auto pb-20">
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-100 safe-area-inset-bottom">
          <div className="flex items-center justify-around h-16 px-2">
            {primaryNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center w-16 h-14 rounded-2xl transition-all active:scale-95 ${
                  isActive(item.path)
                    ? 'text-pink-600'
                    : 'text-gray-400'
                }`}
              >
                <item.icon
                  className="w-6 h-6 mb-0.5"
                  filled={isActive(item.path)}
                />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            ))}

            {/* More button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className={`flex flex-col items-center justify-center w-16 h-14 rounded-2xl transition-all active:scale-95 ${
                isSecondaryActive ? 'text-pink-600' : 'text-gray-400'
              }`}
            >
              <MoreIcon className="w-6 h-6 mb-0.5" />
              <span className="text-[10px] font-medium">More</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Desktop Main Content */}
      <div className="hidden md:block flex-1 overflow-auto">
        {children}
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col animate-slide-in-right">
            {/* Menu Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-semibold">
                  {getExpertInitials()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {expert?.display_name || 'Expert'}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {expert?.email || ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 p-4 overflow-y-auto">
              {/* Profile Switcher for Mobile */}
              <div className="mb-4">
                <ProfileSwitcher currentProfile="expert" compact />
              </div>

              <div className="mb-4">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 px-3">Settings</p>
                <ul className="space-y-1">
                  {secondaryNavItems.map((item) => (
                    <li key={item.path}>
                      <button
                        onClick={() => {
                          navigate(item.path);
                          setMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${
                          isActive(item.path)
                            ? 'bg-pink-50 text-pink-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'text-pink-600' : 'text-gray-400'}`} />
                        <span>{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleLogoutClick}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal - rendered via portal to document.body */}
      {showLogoutConfirm && createPortal(
        <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="logout-modal" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={handleCancelLogout}
          />

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 transform transition-all">
              {/* Icon */}
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Log out?
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to log out of your account?
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelLogout}
                  className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmLogout}
                  className="flex-1 py-2.5 px-4 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors active:scale-[0.98]"
                >
                  Log out
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Icon components with optional filled prop for active state
interface IconProps {
  className?: string;
  filled?: boolean;
}

const CalendarIcon: React.FC<IconProps> = ({ className, filled }) => (
  <svg className={className} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 0 : 1.75} viewBox="0 0 24 24">
    {filled ? (
      <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    )}
  </svg>
);

const BookingsIcon: React.FC<IconProps> = ({ className, filled }) => (
  <svg className={className} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 0 : 1.75} viewBox="0 0 24 24">
    {filled ? (
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    )}
  </svg>
);

const ClientsIcon: React.FC<IconProps> = ({ className, filled }) => (
  <svg className={className} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 0 : 1.75} viewBox="0 0 24 24">
    {filled ? (
      <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    )}
  </svg>
);

const ServicesIcon: React.FC<IconProps> = ({ className, filled }) => (
  <svg className={className} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 0 : 1.75} viewBox="0 0 24 24">
    {filled ? (
      <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    )}
  </svg>
);

const ClockIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IntegrationsIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const SettingsIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const MoreIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

export default ExpertLayout;
